import { Box, Button, Flex, Heading, Spinner, Text } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bffApi, useListMyNotificationsQuery, useMarkNotificationsAsPeekedMutation, type NotificationDto } from "../../autogen/bff-api";
import { ErrorBoundary } from "../../common/errors/ErrorBoundary/ErrorBoundary";
import { useApiError } from "../../common/errors/useApiError";
import { useAppDispatch } from "../../common/redux/hooks";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { NotificationRow } from "../../common/notifications/NotificationRow";

const LIMIT = 20;

export const NotificationsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();
  const { data, isLoading, isError } = useListMyNotificationsQuery({ limit: LIMIT, offset: 0 });
  const [markAsPeeked] = useMarkNotificationsAsPeekedMutation();
  const [extra, setExtra] = useState<NotificationDto[]>([]);
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(false);

  const all = [...(data?.notifications ?? []), ...extra];

  const peekUnseen = useCallback(
    (items: NotificationDto[]) => {
      const ids = items.filter((n) => !n.peeked).map((n) => n.id);
      // Background bookkeeping: swallow failures (no toast) but consume the promise so a rejection isn't unhandled.
      if (ids.length > 0) void markAsPeeked({ markNotificationsRequest: { notificationIds: ids } }).unwrap().catch(() => undefined);
    },
    [markAsPeeked]
  );

  useEffect(() => {
    if (data?.notifications) peekUnseen(data.notifications);
    // Keyed on the page-1 query result; peeking later pages happens in loadMore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadMore = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatch(bffApi.endpoints.listMyNotifications.initiate({ limit: LIMIT, offset: all.length })).unwrap();
      if (res.notifications.length < LIMIT) setExhausted(true);
      setExtra((prev) => [...prev, ...res.notifications]);
      peekUnseen(res.notifications);
    } catch (e) {
      displayer.triggerSoft(e);
    } finally {
      setLoading(false);
    }
  }, [dispatch, all.length, displayer, peekUnseen]);

  return (
    <Box maxW="container.md" mx="auto">
      <Heading as="h1" size="lg" color="smText" mb="6">{t("Alerts")}</Heading>
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
        {isLoading ? (
          <Flex py="16" justify="center"><Spinner color="smPrimary" /></Flex>
        ) : isError ? (
          <Flex py="16" justify="center"><Text color="smError">{t("Could not load alerts.")}</Text></Flex>
        ) : all.length === 0 ? (
          <Flex py="16" justify="center"><Text color="smMuted">{t("You're all caught up")}</Text></Flex>
        ) : (
          all.map((n, i) => (
            <Box key={n.id} borderTop={i === 0 ? "none" : "1px solid"} borderColor="smBorder">
              <ErrorBoundary
                scope={`notification-row-${n.id}`}
                fallback={<Flex px="4" py="3"><Text color="smMuted" fontSize="sm">{t("Couldn't show this alert.")}</Text></Flex>}
              >
                <NotificationRow notification={n} selectedOrgId={authState.selectedOrg.id} />
              </ErrorBoundary>
            </Box>
          ))
        )}
      </Box>
      {!exhausted && all.length >= LIMIT && (
        <Flex justify="center" mt="4">
          <Button variant="outline" onClick={loadMore} isLoading={loading}>{t("Load more")}</Button>
        </Flex>
      )}
    </Box>
  );
};
